import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "ib-install-dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 2500);
    }

    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      /* user dismissed */
    }
    setDeferred(null);
    dismiss();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -24 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          data-testid="install-prompt-banner"
          className="sm:hidden fixed left-3 right-3 z-[60] top-[calc(4.25rem+env(safe-area-inset-top))] hero-glass rounded-2xl border border-ib-blue/30 shadow-xl shadow-black/30 px-3 py-2.5 flex items-center gap-3"
        >
          <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-ib-blue flex items-center justify-center">
            <Download className="w-4 h-4 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-outfit font-semibold text-sm text-ib-text leading-tight">
              Install <span className="font-unbounded font-black">Iron</span>
              <span className="text-ib-blue">Byte</span> app
            </p>
            {iosHint ? (
              <p className="font-manrope text-[11px] text-ib-text-secondary leading-tight mt-0.5 flex items-center gap-1 flex-wrap">
                Tap <Share className="inline w-3 h-3" /> then "Add to Home Screen"
              </p>
            ) : (
              <p className="font-manrope text-[11px] text-ib-text-secondary leading-tight mt-0.5">
                Add it to your home screen for one-tap access
              </p>
            )}
          </div>
          {!iosHint && (
            <button
              type="button"
              onClick={install}
              data-testid="install-prompt-install-btn"
              className="flex-shrink-0 font-outfit font-semibold text-xs text-white bg-ib-blue px-3 py-2 rounded-full active:scale-95 transition-transform"
            >
              Install
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            data-testid="install-prompt-dismiss-btn"
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-ib-text-muted hover:text-ib-text"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
